// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { TagEntity } from '@shared/data-access/interfaces/redux/tags-management-state-types';

export interface UseTagManagementProps {
  objectType: string;
  objectId: string;
  allTags: readonly TagEntity[];
  initialAssignedTags: readonly TagEntity[];
  onChange?: (tagIds: number[]) => void;
  onCreateTag?: (name: string) => void;
}

const tempTagId = () => -Math.abs(Date.now());

export const useTagManagement = (props: UseTagManagementProps) => {
  const { objectId, allTags, initialAssignedTags, onChange, onCreateTag } = props;

  const [assignedTags, setAssignedTags] = useState<TagEntity[]>(() => [
    ...initialAssignedTags,
  ]);

  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setAssignedTags([...initialAssignedTags]);
  }, [objectId, initialAssignedTags]);

  const assignedTagIds = useMemo(
    () => assignedTags.map(tag => tag.tag_id),
    [assignedTags],
  );

  const assignedSet = useMemo(() => new Set(assignedTagIds), [assignedTagIds]);

  const filteredTags = useMemo(
    () =>
      value.trim() === ''
        ? allTags.filter(tag => !assignedSet.has(tag.tag_id))
        : allTags.filter(
            tag =>
              !assignedSet.has(tag.tag_id) &&
              tag.name.toLowerCase().includes(value.trim().toLowerCase()),
          ),
    [allTags, assignedSet, value],
  );

  const canCreate =
    value.trim() !== '' &&
    !allTags.some(t => t.name.toLowerCase() === value.trim().toLowerCase());

  const showDropdown = open && (filteredTags.length > 0 || canCreate);

  const notifyChange = useCallback(
    (nextTags: TagEntity[]) => {
      const ids = nextTags.map(t => t.tag_id).filter((id): id is number => id > 0);
      onChange?.(ids);
    },
    [onChange],
  );

  const onAddTag = useCallback(
    (tag: TagEntity) => {
      if (assignedSet.has(tag.tag_id)) return;
      const next = [...assignedTags, tag];
      setAssignedTags(next);
      notifyChange(next);
    },
    [assignedTags, assignedSet, notifyChange],
  );

  const onRemoveTag = useCallback(
    (tagId: number) => {
      const next = assignedTags.filter(t => t.tag_id !== tagId);
      setAssignedTags(next);
      notifyChange(next);
    },
    [assignedTags, notifyChange],
  );

  const onConfirmCreate = useCallback(() => {
    const name = value.trim();
    if (!name || !onCreateTag) return;
    const tempTag: TagEntity = { tag_id: tempTagId(), name };
    setAssignedTags(prev => [...prev, tempTag]);
    setValue('');
    setOpen(false);
    onCreateTag(name);
  }, [value, onCreateTag]);

  return {
    assignedTags,
    value,
    setValue,
    open,
    setOpen,
    filteredTags,
    canCreate,
    showDropdown,
    onAddTag,
    onRemoveTag,
    onConfirmCreate,
  };
};
